// =============================================================================
// BP-08-05: Vendor Portal Frontend - VendorOrdersPage
// =============================================================================

import React from 'react';
import { Search, Filter, Download } from 'lucide-react';
import { VendorHeader } from '@/features/vendor/components/VendorHeader';
import { OrderTable } from '@/features/vendor/components/OrderTable';
import { useVendorOrders, useConfirmOrder, useShipOrder } from '@/features/vendor/hooks/useVendorOrders';
import { useVendorStore } from '@/features/vendor/stores/vendorStore';
import type { OrderStatus } from '@/features/vendor/types/vendorTypes';

const ORDER_STATUSES: { value: OrderStatus | ''; label: string }[] = [
  { value: '', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'processing', label: 'Processing' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function VendorOrdersPage() {
  const { orderFilters, setOrderFilters } = useVendorStore();
  const { data, isLoading, fetchNextPage, hasNextPage } = useVendorOrders(orderFilters);
  const confirmOrder = useConfirmOrder();
  const shipOrder = useShipOrder();

  const orders = data?.pages.flatMap((page) => page.items) || [];

  const handleConfirm = async (orderId: string) => {
    await confirmOrder.mutateAsync(orderId);
  };

  const handleShip = async (orderId: string) => {
    const trackingNumber = prompt('Enter tracking number:');
    if (trackingNumber) {
      await shipOrder.mutateAsync({ orderId, trackingNumber });
    }
  };

  return (
    <div className="min-h-screen">
      <VendorHeader
        title="Orders"
        subtitle={`${orders.length} orders total`}
        actions={
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-navy font-medium rounded-xl hover:bg-cream-dark transition-colors">
            <Download className="w-5 h-5" />
            Export
          </button>
        }
      />

      <div className="p-6">
        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search orders..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta"
                />
              </div>
            </div>

            <select
              value={orderFilters.status || ''}
              onChange={(e) => setOrderFilters({ status: (e.target.value as OrderStatus) || undefined })}
              className="px-4 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-terracotta/20"
            >
              {ORDER_STATUSES.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>

            <input
              type="date"
              value={orderFilters.startDate || ''}
              onChange={(e) => setOrderFilters({ startDate: e.target.value || undefined })}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-terracotta/20"
            />

            <input
              type="date"
              value={orderFilters.endDate || ''}
              onChange={(e) => setOrderFilters({ endDate: e.target.value || undefined })}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-terracotta/20"
            />
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-xl border border-gray-200">
          <OrderTable
            orders={orders}
            isLoading={isLoading}
            onConfirm={handleConfirm}
            onShip={handleShip}
          />

          {hasNextPage && (
            <div className="p-4 text-center border-t border-gray-200">
              <button
                onClick={() => fetchNextPage()}
                className="px-6 py-2 text-terracotta font-medium hover:bg-cream-dark rounded-lg transition-colors"
              >
                Load More Orders
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VendorOrdersPage;

